import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// Cache inteligente com TTL otimizado e prefetch
export class PerformanceCache {
  private static instance: PerformanceCache;
  private cache = new Map<string, { data: any; expiry: number; priority: 'high' | 'medium' | 'low' }>();
  private readonly TTL = {
    high: 30 * 60 * 1000,    // 30 minutos para dados críticos
    medium: 15 * 60 * 1000,  // 15 minutos para dados normais
    low: 5 * 60 * 1000       // 5 minutos para dados temporários
  };

  static getInstance() {
    if (!PerformanceCache.instance) {
      PerformanceCache.instance = new PerformanceCache();
    }
    return PerformanceCache.instance;
  }

  set(key: string, data: any, priority: 'high' | 'medium' | 'low' = 'medium') {
    this.cache.set(key, {
      data,
      expiry: Date.now() + this.TTL[priority],
      priority
    });
  }

  get(key: string) {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return item.data;
  }

  invalidate(pattern?: string) {
    if (!pattern) {
      this.cache.clear();
      return;
    }
    
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }

  prefetch(key: string, fetcher: () => Promise<any>, priority: 'high' | 'medium' | 'low' = 'medium') {
    // Prefetch apenas se não existe no cache
    if (!this.get(key)) {
      fetcher().then(data => this.set(key, data, priority));
    }
  }

  getStats() {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.entries()).map(([key, value]) => ({
        key,
        priority: value.priority,
        expiresIn: Math.max(0, value.expiry - Date.now())
      }))
    };
  }
}

export function usePerformanceCache() {
  const cache = useMemo(() => PerformanceCache.getInstance(), []);
  const queryClient = useQueryClient();

  const invalidateQueries = useCallback((pattern?: string) => {
    cache.invalidate(pattern);
    if (pattern) {
      queryClient.invalidateQueries({ queryKey: [pattern] });
    } else {
      queryClient.clear();
    }
  }, [cache, queryClient]);

  const prefetchData = useCallback((key: string, fetcher: () => Promise<any>, priority: 'high' | 'medium' | 'low' = 'medium') => {
    cache.prefetch(key, fetcher, priority);
  }, [cache]);

  return {
    cache,
    invalidateQueries,
    prefetchData,
    getStats: cache.getStats.bind(cache)
  };
}

// Hook para lazy loading de imagens com fallback
export function useLazyImage(src: string, fallback = '/placeholder.svg') {
  const [imageSrc, setImageSrc] = useState(fallback);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!src) return;

    setIsLoading(true);
    setHasError(false);

    const img = new Image();
    img.onload = () => {
      setImageSrc(src);
      setIsLoading(false);
    };
    img.onerror = () => {
      setHasError(true);
      setIsLoading(false);
      setImageSrc(fallback);
    };
    img.src = src;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src, fallback]);

  return { imageSrc, isLoading, hasError };
}

// Hook para prefetch de dados críticos
export function usePrefetch() {
  const { prefetchData } = usePerformanceCache();
  const queryClient = useQueryClient();

  const prefetchRestaurants = useCallback(() => {
    prefetchData(
      'restaurants-prefetch',
      () => queryClient.prefetchQuery({
        queryKey: ['restaurants'],
        staleTime: 5 * 60 * 1000
      }),
      'high'
    );
  }, [prefetchData, queryClient]);

  const prefetchCategories = useCallback(() => {
    prefetchData(
      'categories-prefetch',
      () => queryClient.prefetchQuery({
        queryKey: ['categories'],
        staleTime: 10 * 60 * 1000
      }),
      'medium'
    );
  }, [prefetchData, queryClient]);

  return {
    prefetchRestaurants,
    prefetchCategories
  };
}